<template>
  <div>
    <b-modal
      :id="modalId"
      :size="modalSize"
      no-close-on-backdrop
      no-close-on-esc
      no-fade
      no-stacking
      hide-footer
      centered
      @show=";(isModalOpen = true), getSubscriptionInformation()"
      @hide="isModalOpen = false"
    >
      <template #modal-header>
        <div>
          <h2 class="modal-title" data-test-hook="new-subscription-modal-header">
            <div>
              <i class="far fa-badge-dollar colorOrange mr-1" data-test-hook="new-subscription-modal-header-icon" />
              {{ modalTitle }}
            </div>
          </h2>
        </div>
      </template>
      <div v-if="isLoadingSubscriptionData">
        <b-row class="text-center">
          <b-col>
            <b-spinner style="width: 10rem; height: 10rem" variant="warning" label="Spinning"></b-spinner>
          </b-col>
        </b-row>
      </div>
      <div v-else>
        <NewHsbSubscriptionView
          v-if="activeView === subscriptionViews.NEW_HSB"
          :apparatus-details="apparatusDetails"
          :apparatus-id="apparatusId"
          :annual-paid-product="annualPaidProduct"
          :monthly-paid-product="monthlyPaidProduct"
          :account-data="accountProfile"
          :selected-product="selectedProduct"
          :is-creating-purchase-intent="isCreatingPurchaseIntent"
          @openPayment="proceedToPayment"
          @setSelectedProduct="setSelectedProduct"
        />
        <AddApparatusUpgradeFromLegacy
          v-else-if="activeView === subscriptionViews.EXISTING_LEGACY"
          :account-data="accountProfile"
          :selected-product="selectedProduct"
          :apparatus-details="apparatusDetails"
          :legacy-subscriptions="legacySubscriptions"
          :apparatus-list="apparatusList"
          :annual-paid-product="annualPaidProduct"
          :monthly-paid-product="monthlyPaidProduct"
          :is-creating-purchase-intent="isCreatingPurchaseIntent"
          @openPayment="proceedToPayment"
          @setSelectedProduct="setSelectedProduct"
        />

        <NewTankSubscriptionView
          v-else-if="activeView === subscriptionViews.NEW_TANK"
          :selected-product="selectedProduct"
          :annual-paid-product="annualPaidProduct"
          :monthly-paid-product="monthlyPaidProduct"
          :is-creating-purchase-intent="isCreatingPurchaseIntent"
          @setSelectedProduct="setSelectedProduct"
          @openPayment="proceedToPayment"
        />
        <ExistingSubscriptionView
          v-else-if="activeView === subscriptionViews.EXISTING_SUB"
          :apparatus-id="apparatusId"
          :account-profile="accountProfile"
          :apparatus-details="apparatusDetails"
          :existing-subscriptions="existingSubscriptions"
          :apparatus-list="apparatusList"
          :modal-id="modalId"
        />
        <AddTankSubscriptionWithBasic
          v-else-if="activeView === subscriptionViews.NEW_TANK_WITH_BASIC"
          :selected-product="selectedProduct"
          :apparatus-details="apparatusDetails"
          :basic-hsb-list="basicHsbList"
          :annual-paid-product="annualPaidProduct"
          :monthly-paid-product="monthlyPaidProduct"
          :is-creating-purchase-intent="isCreatingPurchaseIntent"
          @openPayment="proceedToPayment"
          @setSelectedProduct="setSelectedProduct"
        />
        <AddApparatusUpgradeSubscriptionEnrollment
          v-else-if="activeView === subscriptionViews.UPGRADE_SINGLE_MULTI"
          :isEnrollment="true"
          :new-subscription="selectedProduct"
          :current-subscription="existingSubscriptions[0]"
          :new-apparatuses="[apparatusDetails]"
          :apparatus-list="apparatusList"
          :is-creating-purchase-intent="isCreatingPurchaseIntent"
          @openPayment="proceedToPayment"
        />
        <SubscriptionInfoError v-else-if="activeView === subscriptionViews.ERROR" @retry="getSubscriptionInformation" />
        <b-row class="mt-3" v-if="activeView !== subscriptionViews.ERROR">
          <b-col class="text-center">
            <p class="text-small legal-text">*{{ $t('xPushNotificationsAreAvailableWithInstallation') }}</p>
          </b-col>
        </b-row>
      </div>
    </b-modal>
    <PaymentModal
      :modal-id="`${modalId}-payment`"
      :selected-product="selectedProduct"
      :account-profile="accountProfile"
      @submit="payForProduct"
      @goBack="returnToSubscriptions"
    />
  </div>
</template>

<script>
import * as Constants from '@/constants'

import NewHsbSubscriptionView from './AddHsbNewSubscription.vue'
import NewTankSubscriptionView from './AddTankNewSubscription.vue'
import ExistingSubscriptionView from './AddApparatusExistingSub.vue'
import AddTankSubscriptionWithBasic from './AddTankSubscriptionWithBasic.vue'
import AddApparatusUpgradeSubscriptionEnrollment from './AddApparatusUpgradeSubscriptionEnrollment.vue'
import AddApparatusUpgradeFromLegacy from './AddApparatusUpgradeFromLegacy.vue'
import PaymentModal from './PaymentModal.vue'
import SubscriptionInfoError from './SubscriptionInfoError.vue'
import { isFuelMonitorV2 } from '../../../utils/apparatus'
import {
  subscriptionActive,
  subscriptionDetailContainsApparatus,
  subscriptionDetailActive
} from '../../../utils/mixins'

const subscriptionViews = {
  NEW_HSB: 'subscription-hsb-new',
  NEW_TANK: 'subscription-tank-new',
  EXISTING_SUB: 'subscription-existing',
  NEW_TANK_WITH_BASIC: 'subscription-tank-basic-new',
  UPGRADE_SINGLE_MULTI: 'subscription-single-to-multi',
  EXISTING_LEGACY: 'upgrade-legacy',
  ERROR: 'subscription-error'
  // UPGRADE_LEGACY
  // UPGRADE_MULTI
}

const subscriptionDurations = {
  YEARLY: 'yearly',
  MONTHLY: 'monthly'
}

export default {
  /**
   * modalId: UNIQUE identifier for this instance of subscriptionsModal - needs to be unique for $bvModal to handle it properly
   * apparatusId: apparatus being subscribed
   * apparatusList: existing apparatuses on the account - may or may not include apparatusId depending on enrollment flow
   * accountProfile: user data including organizationId
   */
  props: ['modalId', 'apparatusId', 'apparatusList', 'accountProfile'],
  emits: ['setSubscriptionPollingInfo'],
  components: {
    NewHsbSubscriptionView,
    NewTankSubscriptionView,
    AddApparatusUpgradeFromLegacy,
    ExistingSubscriptionView,
    AddTankSubscriptionWithBasic,
    AddApparatusUpgradeSubscriptionEnrollment,
    PaymentModal,
    SubscriptionInfoError
  },
  data() {
    /**
     * isLoadingSubscriptionData: used to show loading spinner in place of other views
     * isCreatingPurchaseIntent: used to disable "continue" button and display spinner to indicate the user is proceeding to payment
     * isModalOpen: used to stop service calls from happening before the modal is opened
     * activeView: which scenario/flow the user is in
     * existingSubscriptions: used to determine if the user needs to add to or upgrade an existing sub (should only be existing active subs)
     * legacySubscriptions: used to determine if the user needs to upgrade from legacy to new subs (should only be active legacy subs)
     * annualPaidProduct: selectable subscription with a 12-month duration
     * monthlyPaidProduct: selectable subscription with a 1-month duration
     * selectedProduct: what the user has picked / will subscribe to
     * showGetSubscriptionInfoError: there was an issue fetching available subs
     * getSubscriptionInfoErrorMessage: the actual issue to display
     * basicHsbList: all HSBs on the account that don't have an active subscription - for use when subscribing a tank, which requires a premium sub
     * apparatusDetails: the apparatus being enrolled/subscribed
     * subscriptionViews: enum used to select the scenario the user sees
     */
    return {
      isLoadingSubscriptionData: true,
      isCreatingPurchaseIntent: false,
      isModalOpen: false,
      activeView: null,
      existingSubscriptions: [],
      legacySubscriptions: [],
      annualPaidProduct: {},
      monthlyPaidProduct: {},
      selectedProduct: null,
      showGetSubscriptionInfoError: false,
      getSubscriptionInfoErrorMessage: null,
      basicHsbList: null,
      apparatusDetails: null,
      subscriptionViews
    }
  },
  watch: {
    apparatusId: function(newVal, oldVal) {
      if (!oldVal && newVal && this.isModalOpen) {
        this.getSubscriptionInformation()
      }
    }
  },
  methods: {
    async getSubscriptionInformation() {
      if (this.apparatusId && this.isModalOpen) {
        this.isLoadingSubscriptionData = true
        const getApparatusResponse = await this.$store.dispatch('getApparatusById', this.apparatusId)
        if (getApparatusResponse?.data) {
          this.apparatusDetails = getApparatusResponse.data
        }
        if (this.accountProfile?.organizationId) {
          this.$store
            .dispatch('getSubscriptionDetails', this.accountProfile.organizationId)
            .then(async subDetailsResponse => {
              this.existingSubscriptions = subDetailsResponse.data.subscriptions?.filter(
                sub =>
                  sub.status !== Constants.SubscriptionDetailCanceled &&
                  sub.store !== Object.values(Constants.SubscriptionStoreTypes).indexOf('Dealer')
              )
              this.legacySubscriptions = this.getActiveLegacySubscriptions(subDetailsResponse.data)
              const isUpgradeRequired = this.doesApparatusRequireUpgrade(this.apparatusDetails)

              if (this.existingSubscriptions?.length > 0 && !isUpgradeRequired) {
                this.activeView = subscriptionViews.EXISTING_SUB
              } else {
                let apparatusTypeToAdd = ''
                switch (this.apparatusDetails?.apparatusType) {
                  case 0:
                    apparatusTypeToAdd = Constants.HsbApparatusStringType
                    break
                  case 2:
                    apparatusTypeToAdd = Constants.FuelTankApparatusStringType
                    break
                  default:
                    apparatusTypeToAdd = ''
                    break
                }
                if (apparatusTypeToAdd) {
                  // we can't add apparatusTypeToAdd when there are legacy subs present because reasons
                  const availableSubsRequestBody =
                    this.legacySubscriptions?.length > 0
                      ? {
                          organizationId: this.accountProfile.organizationId
                        }
                      : {
                          organizationId: this.accountProfile.organizationId,
                          apparatusTypeToAdd: apparatusTypeToAdd
                        }
                  const availableSubsRequestType =
                    this.legacySubscriptions?.length > 0
                      ? 'getSubscriptionProductsByStoreOrganization'
                      : 'getSubscriptionProductsByStoreOrganizationApparatusToAdd'
                  // await so the loading spinner doesn't go away prematurely
                  await this.$store
                    .dispatch(availableSubsRequestType, availableSubsRequestBody)
                    .then(getProductsResponse => {
                      const subscriptionProducts = getProductsResponse.data.products
                      const eligibleProducts = this.getEligibleSubscriptions(subscriptionProducts)
                      this.annualPaidProduct = eligibleProducts.annualProduct
                      this.monthlyPaidProduct = eligibleProducts.monthlyProduct

                      if (!this.annualPaidProduct || !this.monthlyPaidProduct) {
                        throw new Error()
                      }

                      this.selectedProduct = this.annualPaidProduct // default to yearly

                      if (this.legacySubscriptions?.length > 0) {
                        this.activeView = subscriptionViews.EXISTING_LEGACY
                      } else if (isUpgradeRequired) {
                        if (this.existingSubscriptions?.[0]?.product?.durationInMonths === 1) {
                          this.selectedProduct = this.monthlyPaidProduct
                        }
                        this.activeView = subscriptionViews.UPGRADE_SINGLE_MULTI
                      } else {
                        if (apparatusTypeToAdd === Constants.HsbApparatusStringType) {
                          this.activeView = subscriptionViews.NEW_HSB
                        } else {
                          this.basicHsbList = this.apparatusList?.filter(app => {
                            if (app.type !== Constants.HsbApparatusType || app.isDealerManaged) {
                              return false
                            }

                            // has no legacy products that match the gen's id that are still active
                            if (getProductsResponse?.data?.legacyBillingProducts?.products?.length) {
                              return !getProductsResponse.data?.legacyBillingProducts?.products?.some(
                                prod =>
                                  prod?.apparatusId === app.apparatusId && prod?.status === Constants.SubscriptionActive
                              )
                            }

                            return true
                          })

                          if (this.basicHsbList?.length) {
                            this.activeView = subscriptionViews.NEW_TANK_WITH_BASIC
                          } else {
                            this.activeView = subscriptionViews.NEW_TANK
                          }
                        }
                      }
                    })
                    .catch(error => {
                      this.activeView = subscriptionViews.ERROR
                    })
                } else {
                  this.activeView = subscriptionViews.ERROR
                }
              }
            })
            .catch(error => {
              this.activeView = subscriptionViews.ERROR
            })
            .finally(() => {
              if (this.activeView) {
                this.isLoadingSubscriptionData = false
              }
            })
        }
      }
    },
    proceedToPayment() {
      if (this.selectedProduct && this.selectedProduct.cost > 0) {
        if (this.accountProfile?.organizationId && this.selectedProduct?.productId && this.apparatusId) {
          this.isCreatingPurchaseIntent = true
          const apparatusIds = [this.apparatusId]

          // if there are existing (active) subscriptions, all eligible products should already be on them (addressed below this block)
          if (!this.existingSubscriptions?.length) {
            this.apparatusList?.forEach(app => {
              // if it's an HSB or FM that isn't dealer managed...
              if (
                (app.type === Constants.HsbApparatusType || app.type === Constants.FuelTankApparatusType) &&
                !app.isDealerManaged
              ) {
                // and isn't a v1 fuel monitor...
                if (!(app.type === Constants.FuelTankApparatusType && !isFuelMonitorV2(app))) {
                  // there should be no active legacy subs (if they are, they should be cancelled in that modal), so...
                  // ... we add to the list
                  apparatusIds.push(app.apparatusId)
                }
              }
            })
          }

          // note: when upgrading from one new sub to another, we don't need to cancel anything - just create purchase intent and update sub product
          if (this.doesApparatusRequireUpgrade(this.apparatusDetails) && this.existingSubscriptions?.[0]?.apparatuses) {
            apparatusIds.push(...this.existingSubscriptions[0].apparatuses.map(app => app.apparatusId))
          }

          this.$store
            .dispatch('createPurchaseIntent', {
              productId: this.selectedProduct.productId,
              organizationId: this.accountProfile.organizationId,
              apparatusIds: [...new Set(apparatusIds)] // Set() removes duplicates, spread op turns Set into array
            })
            .then(purchaseIntentResult => {
              if (purchaseIntentResult?.data?.organizationAccountId) {
                this.$bvModal.hide(this.modalId)
                this.$bvModal.show(`${this.modalId}-payment`)
                this.isCreatingPurchaseIntent = false
              } else {
                throw new Error()
              }
            })
            .catch(() => {
              this.isCreatingPurchaseIntent = false
              this.$store.dispatch('displayToast', {
                title: this.$t('xSomethingWentWrong'),
                message: this.$t('xPleaseTryAgain'),
                variant: 'danger'
              })
            })
        }
      } else if (this.selectedProduct && this.selectedProduct.cost === 0) {
        this.$bvModal.hide(this.modalId)
        const appType = this.apparatusDetails?.type || this.apparatusDetails?.apparatusType
        if (appType === Constants.HsbApparatusType) {
          this.$emit('setSubscriptionPollingInfo', null)
          this.openModal('subscription-success')
        } else if (appType === Constants.FuelTankApparatusType) {
          this.$bvModal.show('tank-enrollment-success')
        }
      }
    },
    returnToSubscriptions() {
      this.$bvModal.hide(`${this.modalId}-payment`)
      this.$bvModal.show(this.modalId)
    },
    setSelectedProduct(product) {
      this.selectedProduct = product
      this.$emit('setSelectedProduct', product)
    },
    payForProduct({ resolve, reject }) {
      if (this.accountProfile?.organizationId && this.selectedProduct?.productId && this.apparatusId) {
        const changeSubscriptionMode = {
          organizationId: this.accountProfile.organizationId,
          productId: this.selectedProduct.productId
        }

        this.$store
          .dispatch('updateSubscriptionProduct', changeSubscriptionMode)
          .then(response => {
            // send back success to the payment modal
            resolve(response)
            const appType = this.apparatusDetails?.type || this.apparatusDetails?.apparatusType
            this.$bvModal.hide(`${this.modalId}-payment`)
            if (appType === Constants.HsbApparatusType) {
              this.$emit('setSubscriptionPollingInfo', this.selectedProduct)
              this.openModal('subscription-success')
            } else if (appType === Constants.FuelTankApparatusType) {
              this.$emit('setSubscriptionPollingInfo', this.selectedProduct)
              this.$bvModal.show('tank-enrollment-success')
            }
          })
          .catch(error => {
            let failureMessage = this.$t('xFailedSetUpGeneratorSubscription')
            if (error.response?.data?.message) {
              try {
                let messageAsJson = JSON.parse(error.response.data.message.replace(/\\"/g, '"'))
                if (messageAsJson?.gatewayResponse?.message) {
                  failureMessage = messageAsJson.gatewayResponse.message
                }
              } catch (err) {}
            }
            // send error back to payment modal - show this error and do not proceed
            reject(failureMessage)
          })
      }
    },
    getEligibleSubscriptions(products) {
      if (!this.apparatusList) {
        return null
      }

      if (this.apparatusDetails?.isDealerManaged) {
        return null // dealer-managed products shouldn't even be in this flow, so something has gone wrong if this happens
      }

      const apparatusTypeCounts = this.apparatusList?.reduce(
        (ctr, app) => {
          const newCtr = { ...ctr }
          if (app.isDealerManaged) {
            return newCtr // dealer managed don't count toward subscription eligibility
          }

          if (app.type === Constants.HsbApparatusType) {
            newCtr.hsb = ctr.hsb + 1
          } else if (app.type === Constants.FuelTankApparatusType) {
            // v1 tanks aren't added to subscriptions, nor do they prevent a user from having a single-home subscription
            // HOWEVER, they are included when determining if there are 6 apparatuses (the max) on the account, because of course they are
            if (isFuelMonitorV2(app)) {
              newCtr.tank = ctr.tank + 1
            } else {
              newCtr.tankV1 = ctr.tankV1 + 1
            }
          }

          return newCtr
        },
        {
          hsb: 0,
          tank: 0,
          tankV1: 0
        }
      )

      // sometimes the current apparatus isn't in the apparatusList (because reasons)
      // need to make sure it's counted
      if (!this.apparatusList.some(app => app.apparatusId === this.apparatusId)) {
        if (this.apparatusDetails?.apparatusType === Constants.HsbApparatusType) {
          apparatusTypeCounts.hsb += 1
        } else if (this.apparatusDetails?.apparatusType === Constants.FuelTankApparatusType) {
          // v1 tanks aren't added to subscriptions, nor do they prevent a user from having a single-home subscription
          // HOWEVER, they are included when determining if there are 6 apparatuses (the max) on the account, because of course they are
          if (isFuelMonitorV2(this.apparatusDetails)) {
            apparatusTypeCounts.tank += 1
          } else {
            apparatusTypeCounts.tankV1 += 1
          }
        }
      }

      const apparatusCount = apparatusTypeCounts.hsb + apparatusTypeCounts.tank

      if (apparatusCount + apparatusTypeCounts.tankV1 >= 6) {
        return null
      }

      return products.reduce(
        (final, product) => {
          if (
            apparatusCount <= product.maxApparatuses &&
            apparatusTypeCounts.hsb <= (product.apparatusTypesMax.Generator ?? 0) &&
            apparatusTypeCounts.tank <= (product.apparatusTypesMax.FuelTank ?? 0)
          ) {
            if (product.productId?.includes(subscriptionDurations.MONTHLY) && !final.monthlyProduct) {
              return {
                ...final,
                monthlyProduct: product
              }
            } else if (product.productId?.includes(subscriptionDurations.YEARLY) && !final.annualProduct) {
              return {
                ...final,
                annualProduct: product
              }
            }
          }

          return final
        },
        {
          monthlyProduct: null,
          annualProduct: null
        }
      )
    },
    doesApparatusRequireUpgrade(apparatus) {
      if (this.existingSubscriptions?.length < 1) {
        return false
      }

      const apparatusType = apparatus?.type || apparatus?.apparatusType

      const existingApparatusCount = this.existingSubscriptions[0].apparatuses.reduce((sum, app) => {
        if (app.type === apparatusType) {
          return sum + 1
        }

        return sum
      }, 0)

      const appString = Constants.ApparatusTypeToString[apparatusType]

      const apparatusLimit = this.existingSubscriptions[0].product?.apparatusTypesMax?.[appString] ?? 0

      return existingApparatusCount >= apparatusLimit
    },
    getActiveLegacySubscriptions(subscriptionData) {
      if (!subscriptionData) {
        return []
      }

      return subscriptionData?.legacyBillingProducts?.products?.filter(
        product =>
          product.isLegacy && // is a legacy sub that...
          subscriptionActive(product) && // is active, but is NOT...
          !product.productId.includes('free-wifi') && // a free sub,...
          !(
            product.productId === Constants.DealerManagedProductId ||
            product.productId === Constants.TankUtilityDealerManagedProductId
          ) && // dealer managed, or...
          !this.apparatusList // a v1 tank
            .find(apparatus => apparatus.apparatusId === product.apparatusId)
            ?.properties.find(property => property.name === 'Device' && property.deviceType?.includes('tankutility-v1'))
      )
    }
  },
  computed: {
    modalSize() {
      switch (this.activeView) {
        case subscriptionViews.NEW_HSB:
        case subscriptionViews.UPGRADE_SINGLE_MULTI:
          return 'lg'
        default:
          return 'md'
      }
    },
    modalTitle() {
      switch (this.activeView) {
        case subscriptionViews.EXISTING_SUB:
          return this.$t('xCongratulations') + '!'
        case subscriptionViews.UPGRADE_SINGLE_MULTI:
          return this.$t('xUpgradeYourPlan')
        case subscriptionViews.EXISTING_LEGACY:
          return this.$t('xUpgradeYourPlan')
        default:
          return this.$t('xSubscribeToPremium')
      }
    }
  }
}
</script>
<style lang="scss">
#subscription-modal-tank,
#subscription-modal-hsb {
  .modal-body {
    padding-left: 30px;
    padding-right: 30px;
  }
  .legal-text {
    font-size: 0.875em;
    color: var(--gray-600);
  }
}
</style>
